import { createActionHandler, createReducer, IAction, OperationStatus } from '@pressreader/appstore';
import { userChangeDone } from '@pressreader/authentication-core';
import { CoBrandingInfo, ISponsor } from '@pressreader/sponsorship-types';

import { loadHotspotInfoDone, loadHotspotInfoFailed, partnerInfoChanged, sponsorshipChanged, sponsorshipStateSeeded } from './actions';

interface State {
    loading: OperationStatus;

    sponsor: ISponsor | null;
    partner?: CoBrandingInfo;

    /**
     * Last sponsor display name seen on this user session, kept after `sponsor` is
     * replaced. The post-radiant `SponsorshipManager/get` hands back a zeroed sponsor,
     * so by the time the expiry dialog opens the live sponsor carries no name to brand
     * it with — this survives that replacement. (#175442)
     */
    lastSponsorDisplayName?: string;

    /** Advances on every `sponsorshipChanged`, never on the seed: cache identity for sponsor-dependent content. */
    accessEpoch: number;
}

const initialState: State = {
    loading: OperationStatus.None,

    sponsor: null,
    partner: undefined,
    lastSponsorDisplayName: undefined,
    accessEpoch: 0,
};

const applySponsor = (state: State, { payload: { sponsor } }: IAction<{ sponsor: ISponsor }>): State => {
    // A reader who re-activates at a different venue gets a different sponsor, which must
    // not inherit the previous one's name. Gating on isHotspotActive as well as the id is
    // what keeps that safe: the post-radiant response is an inactive sponsor carrying id
    // "0" (the backend sends CreateInactiveRadiantSponsorshipInfo, whose sponsorId is
    // `default`), so an id check on its own would drop the name in exactly the case it is
    // retained for. Mirrors the carry-forward guards in sponsorship.manager.ts. (#175442)
    const switchedSponsor = !!sponsor?.isHotspotActive && sponsor.id !== state.sponsor?.id;

    return {
        ...state,
        loading: OperationStatus.Done,
        sponsor,
        // Only advance on a sponsor that actually carries a name, so a zeroed one
        // preserves what the active session established instead of erasing it.
        lastSponsorDisplayName: sponsor?.notifications?.sponsoredBy || (switchedSponsor ? undefined : state.lastSponsorDisplayName),
    };
};

const reducer = createReducer(
    initialState,
    createActionHandler(userChangeDone, (state): State => {
        return {
            ...state,
            loading: OperationStatus.None,
            sponsor: null,
            // Drop the retained name with the sponsor: on a shared device the next
            // user must not inherit the previous one's sponsor branding.
            lastSponsorDisplayName: undefined,
        };
    }),
    createActionHandler(loadHotspotInfoDone, applySponsor),
    createActionHandler(loadHotspotInfoFailed, (state): State => {
        return { ...state, loading: OperationStatus.Failed };
    }),
    // Seed and changed write the same sponsor state; only changed advances the epoch,
    // so the seed never moves sponsor-dependent cache keys.
    createActionHandler(sponsorshipChanged, (state, action): State => ({ ...applySponsor(state, action), accessEpoch: state.accessEpoch + 1 })),
    createActionHandler(sponsorshipStateSeeded, applySponsor),
    createActionHandler(partnerInfoChanged, (state, { payload: { partner } }): State => {
        return {
            ...state,
            partner,
        };
    }),
);
export { reducer, State };
